import { useEffect, useMemo } from "react";
import { useGlobal } from "reactn";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import Fuse from "fuse.js";

export const VALID_SEARCH_SECTIONS = {
  TALENTS: "talents",
};

export const SECTIONS = [
  {
    id: VALID_SEARCH_SECTIONS.TALENTS,
    title: "Talents",
    suffix: "",
    path: (className, spec) => `/wow/${spec}/${className}/talents`,
  },
];

function normalize(name) {
  return name.toLowerCase().split(" ").join("_");
}

function toKey(spec, cl_name) {
  return `${cl_name} ${spec}`.toLowerCase().split(" ").join("_");
}

export function useSearchBarPages(options) {
  const { language = "", ssr = false, skip = false, exclude = [] } = options || {};
  const [savedResults, setSavedResults] = useGlobal("search-bar-pages");

  // TODO: Need to get classes from static data
  const url = `https://static.bigbrain.gg/wow/playable-class.json`;
  const classSpecs = useJSONFetcher(url, { ssr, skip });
  //console.log(classSpecs);

  const searchBarPages = useMemo(() => {
    if (savedResults) {
      return savedResults;
    }

    // Build search bar pages
    const FILTERED_SECTIONS = SECTIONS.filter((section) => !exclude.includes(section.id));

    const specPages = FILTERED_SECTIONS.map((curSection) => {
      const pages = (classSpecs.data ?? []).reduce((accSpecs, curClass) => {
        const { name, specs, id } = curClass;
        const specPages = specs.map((spec) => ({
          key: `${spec.name.toLowerCase()} ${name.toLowerCase()}`,
          imgKey: toKey(spec.name, name),
          displayName: `${spec.name} ${name}${curSection.suffix}`,
          url: curSection.path(normalize(name), normalize(spec.name)),
          id,
          section: curSection.title,
        }));

        return accSpecs.concat(specPages);
      }, []);

      return { title: curSection.title, pages };
    });

    return [...specPages];
  }, [savedResults, classSpecs]);

  // Save results globally so we don't have to build the pages again
  useEffect(() => {
    if (!savedResults && !classSpecs.loading && searchBarPages) {
      setSavedResults(searchBarPages);
    }
  }, [savedResults, searchBarPages]);

  // TODO: Setup loading from fetch
  if (classSpecs.loading) {
    return { data: null, error: false, loading: !classSpecs.loading };
  } else if (classSpecs.error) {
    return { data: null, loading: false, error: !classSpecs.error };
  } else {
    return { data: searchBarPages, loading: false, error: false };
  }
}

export function useSearchPages(options) {
  const { language = "", ssr = false, skip = false, exclude = [] } = options || {};
  const [savedResults, setSavedResults] = useGlobal("search-pages");

  // TODO: Need to get classes from static data
  const url = `https://static.bigbrain.gg/wow/playable-class.json`;
  const classSpecs = useJSONFetcher(url, { ssr, skip });

  const searchPages = useMemo(() => {
    if (savedResults && savedResults.length > 0) {
      return savedResults;
    }

    // Build search bar pages
    const classPages = (classSpecs.data ?? []).reduce((accSpecs, curClass) => {
      const { name, specs, id } = curClass;
      const specPages = specs.map((spec) => ({
        key: `${spec.name.toLowerCase()} ${name.toLowerCase()}`,
        displayName: `${spec.name} ${name}`,
        url: `/wow/${normalize(spec.name)}/${normalize(name)}/talents`,
        imgKey: toKey(spec.name, name),
        id,
        section: "Talents",
      }));

      return accSpecs.concat(specPages);
    }, []);

    return [...classPages];
  }, [savedResults, classSpecs]);

  // Save results globally so we don't have to build the pages again
  useEffect(() => {
    if (!savedResults && searchPages) {
      setSavedResults(searchPages);
    }
  }, [savedResults, searchPages]);

  if (classSpecs.loading) {
    return { data: null, error: false, loading: !classSpecs.loading };
  } else if (classSpecs.error) {
    return { data: null, loading: false, error: !classSpecs.error };
  } else {
    return { data: searchPages, loading: false, error: false };
  }
}

export function useBestSearchMatch() {
  const { data: searchPages } = useSearchPages();
  const fuseOpts = {
    shouldSort: true,
    includeScore: true,
    // tokenize: true,
    // matchAllTokens: true,
    keys: ["key"],
  };

  return (query) => {
    if (searchPages) {
      var fuse = new Fuse(searchPages, fuseOpts);
      let res = fuse.search((query && query.trim()) || "");

      return res;
    } else {
      return [];
    }
  };
}
